import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { MdClose } from 'react-icons/md'
import { Header } from './styles'
import { Flex, FormLabel, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react'
import { iAvatarXInformacoes, iNomeId } from '../../../../../../interfaces'
import { Button } from '../../../../../../components/Button'
import { FindUser } from '../../../../../../components/FindBox'
import { SelectCmp } from '../../../../../../components/SelectCmp'
import { appApi } from '../../../../../../services/appApi'
import { ElementoTooltip } from '../../../../../../components/ElementoTooltip'

interface iColaboradorOption extends iNomeId {
  departamentoId?: string
  squadsId?: string[]
}

interface iReuniaoPar {
  responsavel: iAvatarXInformacoes
  participante: iAvatarXInformacoes
}

interface iModalAddAvaliado {
  onRequestClose: () => void
  onConfirm: (reunioes: iReuniaoPar[]) => void
  exists: (responsavelId: string, participanteId: string) => boolean
}

export const ModalAdicionarReuniao: React.FC<iModalAddAvaliado> = ({ onConfirm, onRequestClose, exists }) => {
  Modal.setAppElement('#root')
  const [Options, setOptions] = useState<iColaboradorOption[]>([])
  const [Areas, setAreas] = useState<iNomeId[]>([])
  const [Squads, setSquads] = useState<iNomeId[]>([])
  const [Responsavel, setResponsavel] = useState<iAvatarXInformacoes>()
  const [Participante, setParticipante] = useState<iAvatarXInformacoes>()
  const [AreaId, setAreaId] = useState('')
  const [SquadId, setSquadId] = useState('')
  const [Tipo, setTipo] = useState<'participante' | 'area' | 'squad'>('participante')
  const [TipoResponsavel, setTipoResponsavel] = useState<'gestor' | 'outro'>('outro')

  function getSelectColaboradores(): void {
    appApi.get('Colaborador/Select')
      .then(res => setOptions(res.data))
      .catch(err => console.log(err))
  }

  function ObterAreas(): void {
    appApi.get('Departamento/Select')
      .then(res => setAreas(res.data))
      .catch(err => console.log(err))
  }

  function ObterSquads(): void {
    appApi.get('Tag/Select?tipo=2')
      .then(res => setSquads(res.data))
      .catch(err => console.log(err))
  }

  function PertenceArea(colab: iColaboradorOption, areaId: string): boolean {
    return colab.departamentoId === areaId
  }

  function PertenceSquad(colab: iColaboradorOption, squadId: string): boolean {
    return colab.squadsId?.includes(squadId) ?? false
  }

  function ParaAvatar(colab: iColaboradorOption): iAvatarXInformacoes {
    return { id: colab.id, avatar: colab.avatar ?? '', info1: colab.nome }
  }

  // Responsável do par: o escolhido em "Outro" ou o gestor direto do participante em "Gestor"
  function ObterResponsavel(colab: iColaboradorOption): iAvatarXInformacoes | undefined {
    if (TipoResponsavel === 'outro') return Responsavel
    const gestor = Options.find(e => e.id === colab.gestorId)
    return gestor ? ParaAvatar(gestor) : undefined
  }

  // Só o tipo selecionado conta; os valores dos outros tipos ficam guardados
  const Selecionados = Options.filter(e =>
    (Tipo === 'participante' && e.id === Participante?.id) ||
    (Tipo === 'area' && AreaId !== '' && PertenceArea(e, AreaId)) ||
    (Tipo === 'squad' && SquadId !== '' && PertenceSquad(e, SquadId))
  )

  const Reunioes: iReuniaoPar[] = Selecionados
    .map(e => ({ responsavel: ObterResponsavel(e), participante: ParaAvatar(e) }))
    .filter((e): e is iReuniaoPar =>
      !!e.responsavel &&
      e.responsavel.id !== e.participante.id &&
      !exists(e.responsavel.id, e.participante.id)
    )

  const SemGestor = TipoResponsavel === 'gestor' ? Selecionados.filter(e => !ObterResponsavel(e)) : []

  const AreasComColaboradores = Areas.filter(e => Options.some(r => PertenceArea(r, e.id)))
  const SquadsComColaboradores = Squads.filter(e => Options.some(r => PertenceSquad(r, e.id)))

  function SelecionarParticipante(id: string): void {
    const colab = Options.find(e => e.id === id)
    if (colab) {
      setParticipante({
        id: colab.id,
        avatar: colab.avatar ?? '',
        info1: colab.nome
      })
    }
  }

  function SelecionarResponsavel(id: string): void {
    const colab = Options.find(e => e.id === id)
    if (colab) {
      setResponsavel({
        id: colab.id,
        avatar: colab.avatar ?? '',
        info1: colab.nome
      })
    }
  }

  useEffect(() => {
    getSelectColaboradores()
    ObterAreas()
    ObterSquads()
  }, [])

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      overlayClassName='react-modal-overlay'
      className='react-modal-content-default'
    >
      <Header>
        <Flex justifyContent={'space-between'}>
          <div>
            <h2>Adicione um avaliado</h2>
            <span>O usuário deve estar cadastrado no sistema para ser adicionado como participante na pesquisa. No entanto, seus stakeholders podem ser adicionados apenas com o e-mail, posteriormente na lista de participantes</span>
          </div>
          <MdClose color='var(--a3)' cursor={'pointer'} onClick={onRequestClose} />
        </Flex>
      </Header>
      <Flex gap={'1rem'} flexDir={'column'} padding={'1rem'}>
        <Flex flexDir={'column'}>
          <FormLabel>Responsável</FormLabel>
          <RadioGroup value={TipoResponsavel} onChange={(e) => setTipoResponsavel(e as 'gestor' | 'outro')}>
            <Stack direction='row' gap={'1rem'}>
              <Radio value='gestor'>Gestor</Radio>
              <Radio value='outro'>Outro</Radio>
            </Stack>
          </RadioGroup>
        </Flex>

        {TipoResponsavel === 'outro' &&
          <Flex flexDir={'column'}>
            <FormLabel>Nome do responsável</FormLabel>
            <FindUser
              lista={Options.filter(e => e.id !== Participante?.id)}
              placeholder='Digite o nome do responsável'
              onChoice={(e) => SelecionarResponsavel(e)}
              selecionado={Responsavel?.id}
            />
          </Flex>
        }

        <Flex flexDir={'column'}>
          <FormLabel>Adicionar participantes por</FormLabel>
          <RadioGroup value={Tipo} onChange={(e) => setTipo(e as 'participante' | 'area' | 'squad')}>
            <Stack direction='row' gap={'1rem'}>
              <Radio value='participante'>Colaborador</Radio>
              <Radio value='area'>Área</Radio>
              <Radio value='squad'>Squad</Radio>
            </Stack>
          </RadioGroup>
        </Flex>

        {Tipo === 'participante' &&
          <Flex flexDir={'column'}>
            <FormLabel>Nome do participante</FormLabel>
            <FindUser
              lista={Options.filter(e => e.id !== Responsavel?.id)}
              placeholder='Digite o nome do participante'
              onChoice={(e) => SelecionarParticipante(e)}
              selecionado={Participante?.id}
            />
          </Flex>
        }

        {Tipo === 'area' &&
          <SelectCmp title='Área' value={AreaId} OnChange={setAreaId}>
            <option value={''}>Selecione uma área</option>
            {AreasComColaboradores.map((e) => <option key={e.id} value={e.id}>{e.nome} ({Options.filter(r => PertenceArea(r, e.id)).length})</option>)}
          </SelectCmp>
        }

        {Tipo === 'squad' &&
          <SelectCmp title='Squad' value={SquadId} OnChange={setSquadId}>
            <option value={''}>Selecione um squad</option>
            {SquadsComColaboradores.map((e) => <option key={e.id} value={e.id}>{e.nome} ({Options.filter(r => PertenceSquad(r, e.id)).length})</option>)}
          </SelectCmp>
        }
        {Reunioes.length > 0 &&
          <Text fontSize={'.875rem'}>
            {Reunioes.length} reunião(ões): {Reunioes.map(e => TipoResponsavel === 'gestor' ? `${e.participante.info1} (gestor: ${e.responsavel.info1})` : e.participante.info1).join(', ')}
          </Text>
        }
        {SemGestor.length > 0 &&
          <Text fontSize={'.875rem'} color={'var(--Red)'}>
            Sem gestor cadastrado, não serão adicionados: {SemGestor.map(e => e.nome).join(', ')}
          </Text>
        }
      </Flex>
      <Flex alignItems={'center'} justifyContent={'end'} padding={'0rem 1rem 1rem 0rem'}>
        <ElementoTooltip label={TipoResponsavel === 'outro' && !Responsavel ? 'Selecione o responsável' : Reunioes.length === 0 ? (Selecionados.length > 0 ? 'Nenhuma reunião nova para adicionar' : 'Selecione um participante, uma área ou um squad') : ''}>
          <Button
            size='lg'
            VarColor='v2'
            isDisabled={Reunioes.length === 0}
            onClick={() => {
              onConfirm(Reunioes)
              onRequestClose()
            }}
          >Adicionar{Reunioes.length > 1 ? ` ${Reunioes.length} reuniões` : ''}</Button>
        </ElementoTooltip>
      </Flex>
    </Modal>
  )
}
